import React, { useEffect, useRef, useState } from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__turnstileReady&render=explicit";

// Single global loader so multiple widgets on the page reuse the same script tag.
let scriptLoadingPromise = null;
function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  if (scriptLoadingPromise) return scriptLoadingPromise;
  scriptLoadingPromise = new Promise((resolve, reject) => {
    window.__turnstileReady = () => resolve();
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error("turnstile-script-load-failed"));
    document.head.appendChild(s);
  });
  return scriptLoadingPromise;
}

/**
 * Cloudflare Turnstile widget. Calls `onToken(token)` when the user completes
 * the challenge, `onToken(null)` when it expires or errors. Uses managed mode
 * (auto-decides interactive vs invisible based on risk signals).
 */
export default function TurnstileWidget({ siteKey, onToken, theme = "light" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!siteKey) {
      setError("CAPTCHA is not configured. Please contact support.");
      return;
    }
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        try {
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            theme,
            callback: (token) => onToken && onToken(token),
            "expired-callback": () => onToken && onToken(null),
            "error-callback": () => {
              setError("Verification failed to load. Please refresh the page.");
              onToken && onToken(null);
            },
            "timeout-callback": () => onToken && onToken(null),
          });
        } catch (e) {
          setError("Could not initialise human-verification widget.");
        }
      })
      .catch(() => {
        if (!cancelled) setError("Could not load human-verification script. Check your connection.");
      });
    return () => {
      cancelled = true;
      try {
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch (e) { /* ignore */ }
    };
  }, [siteKey, theme, onToken]);

  // Expose a reset hook for the parent via a stable function on the DOM node
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    node.__resetTurnstile = () => {
      try {
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      } catch (e) { /* ignore */ }
    };
  });

  return (
    <div className="space-y-2" data-testid="turnstile-widget-wrap">
      <div ref={containerRef} data-testid="turnstile-widget" />
      {error && (
        <p className="text-xs text-[#FF4F00]" data-testid="turnstile-error">{error}</p>
      )}
    </div>
  );
}
